import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Input, Table, Tabs, Tag, Typography, Spin, Button, Modal, Form, Select } from 'antd'
import { TeamOutlined, SearchOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons'
import { useFormik, FormikProvider, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import type { AlvoPreco, RegistrarCaptadorForm } from '@/types'
import { BRAZILIAN_STATES } from '@/lib/constants'
import { usePrecosStore } from '@/stores/precosStore'
import { useRotuloDeFilho } from '@/lib/rotuloDeFilho'
import { FormikInput } from '@/components/FormikInput'
import { buscarEmpresaPorCnpj } from '@/lib/brasilApi'
import {
  formatCnpj,
  formatCpf,
  formatTelefone,
  isValidCnpj,
  isValidCpf,
  stripCpf,
} from '@/lib/formatters'

const { Title, Text } = Typography

// ─── Entry component ─────────────────────────────────────────────────────────

export function Precos() {
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PageHeader />
      <AlvosCard />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function PageHeader() {
  const [modalOpen, setModalOpen] = useState(false)
  const rotulo = useRotuloDeFilho()

  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <Title level={4} className="!mb-1">
          <TeamOutlined className="mr-2" />
          {rotulo.plural}
        </Title>
        <Text type="secondary">
          Cadastre {rotulo.plural.toLowerCase()} e configure os preços dos cursos.
        </Text>
      </div>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
        Cadastrar {rotulo.singular}
      </Button>
      <CaptadorModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  )
}

function AlvosCard() {
  const alvos = usePrecosStore((s) => s.alvos)
  const loading = usePrecosStore((s) => s.alvosLoading)
  const filtro = usePrecosStore((s) => s.filtroAlvo)
  const fetchAlvos = usePrecosStore((s) => s.fetchAlvos)
  const setFiltro = usePrecosStore((s) => s.setFiltroAlvo)

  useEffect(() => {
    fetchAlvos()
  }, [fetchAlvos])

  const filtrados = alvos.filter((alvo) =>
    alvo.nome?.toLowerCase().includes(filtro.trim().toLowerCase()),
  )

  return (
    <Card>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Filtrar por nome"
        value={filtro}
        onChange={(event) => setFiltro(event.target.value)}
        className="mb-4 max-w-md"
      />
      {loading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : (
        <AlvosTable alvos={filtrados} />
      )}
    </Card>
  )
}

function AlvosTable({ alvos }: { alvos: AlvoPreco[] }) {
  const navigate = useNavigate()
  const rotuloDoAlvo = useRotuloDeFilho().singular

  return (
    <Table<AlvoPreco>
      dataSource={alvos}
      rowKey="id"
      pagination={{ pageSize: 20, hideOnSinglePage: true }}
      onRow={(alvo) => ({
        onClick: () => navigate(`/precos/${alvo.id}`),
        className: 'cursor-pointer',
      })}
      columns={[
        {
          title: 'Nome',
          dataIndex: 'nome',
          render: (nome: string, alvo) => (
            <span className="flex items-center gap-2">
              {nome}
              {alvo.parceiroPaiId != null && (
                <Tag color="orange" className="capitalize">
                  {rotuloDoAlvo}
                </Tag>
              )}
            </span>
          ),
        },
        { title: 'CPF/CNPJ', dataIndex: 'cpfCnpj', width: 200 },
      ]}
    />
  )
}

// ─── Cadastro de captador ─────────────────────────────────────────────────────

type TipoDePessoa = 'FISICA' | 'JURIDICA'

const MASCARA_CPF = '999.999.999-99'
const MASCARA_CNPJ = '99.999.999/9999-99'

interface ValoresDoCaptador {
  tipo: TipoDePessoa
  cpfCnpj: string
  nome: string
  telefone: string
  uf: string
  email: string
  nomeUsuario: string
  cpfUsuario: string
}

const valoresIniciais: ValoresDoCaptador = {
  tipo: 'FISICA',
  cpfCnpj: '',
  nome: '',
  telefone: '',
  uf: '',
  email: '',
  nomeUsuario: '',
  cpfUsuario: '',
}

const captadorSchema = Yup.object({
  tipo: Yup.string().oneOf(['FISICA', 'JURIDICA']).required(),
  cpfCnpj: Yup.string()
    .required('Informe o documento')
    .when('tipo', {
      is: 'FISICA',
      then: (schema) => schema.test('cpf', 'CPF inválido', (valor) => isValidCpf(valor ?? '')),
      otherwise: (schema) => schema.test('cnpj', 'CNPJ inválido', (valor) => isValidCnpj(valor ?? '')),
    }),
  nome: Yup.string().required('Informe o nome'),
  telefone: Yup.string().test(
    'telefone',
    'Telefone incompleto',
    (valor) => !valor || valor.replace(/\D/g, '').length >= 10
  ),
  uf: Yup.string(),
  email: Yup.string().email('E-mail inválido').required('Informe o e-mail de acesso'),
  nomeUsuario: Yup.string().when('tipo', {
    is: 'JURIDICA',
    then: (schema) => schema.required('Informe quem vai acessar o painel'),
  }),
  cpfUsuario: Yup.string().when('tipo', {
    is: 'JURIDICA',
    then: (schema) =>
      schema
        .required('Informe o CPF de quem vai acessar')
        .test('cpf', 'CPF inválido', (valor) => isValidCpf(valor ?? '')),
  }),
})

/**
 * Pessoa física é a mesma pessoa dos dois lados — o CPF do captador é o do login, então não faz
 * sentido pedir duas vezes. Pessoa jurídica não tem CPF, e o login é por CPF: aí a pessoa que vai
 * operar o painel precisa se identificar.
 */
function montarCadastro(values: ValoresDoCaptador): RegistrarCaptadorForm {
  const ehPessoaFisica = values.tipo === 'FISICA'
  const documento = ehPessoaFisica ? formatCpf(values.cpfCnpj) : formatCnpj(values.cpfCnpj)

  return {
    cpfCnpj: documento,
    nome: values.nome,
    telefone: values.telefone ? formatTelefone(values.telefone) : undefined,
    uf: values.uf || undefined,
    email: values.email,
    nomeUsuario: ehPessoaFisica ? values.nome : values.nomeUsuario,
    cpfUsuario: ehPessoaFisica ? documento : formatCpf(values.cpfUsuario),
  }
}

function cpfDeAcesso(values: ValoresDoCaptador): string {
  return values.tipo === 'FISICA' ? values.cpfCnpj : values.cpfUsuario
}

// Registers a captador (a parceiro that sells under the acting parceiro) and auto-creates its login
// usuario. The backend rejects an existing parceiro's cpfCnpj and enforces the two-level cap.
function CaptadorModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const registrarCaptador = usePrecosStore((s) => s.registrarCaptador)
  const rotulo = useRotuloDeFilho()

  const formik = useFormik({
    initialValues: valoresIniciais,
    validationSchema: captadorSchema,
    onSubmit: async (values, helpers) => {
      try {
        await registrarCaptador(montarCadastro(values))
        toast.success(
          `Cadastrado! ${rotulo.artigoSingular} entra com o CPF ${cpfDeAcesso(values)} e a senha ` +
            `${stripCpf(cpfDeAcesso(values)).slice(0, 6)}.`
        )
        helpers.resetForm()
        onClose()
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : `Erro ao cadastrar ${rotulo.singular}`
        toast.error(message)
      }
    },
  })

  function handleClose() {
    formik.resetForm()
    onClose()
  }

  return (
    <Modal
      title={`Cadastrar ${rotulo.singular}`}
      open={open}
      onCancel={handleClose}
      onOk={() => formik.handleSubmit()}
      okText="Cadastrar"
      cancelText="Cancelar"
      confirmLoading={formik.isSubmitting}
    >
      <FormikProvider value={formik}>
        <SeletorDeTipoDePessoa />
        <Form layout="vertical" className="mt-4">
          <DocumentoDoCaptador />
          <NomeDoCaptador />
          <FormikInput
            name="telefone"
            label="Telefone"
            mask="(99) 99999-9999"
            placeholder="(00) 00000-0000"
          />
          <SeletorDeUf />
          <DadosDeAcesso />
        </Form>
      </FormikProvider>
    </Modal>
  )
}

function SeletorDeTipoDePessoa() {
  const { values, setValues } = useFormikContext<ValoresDoCaptador>()

  return (
    <Tabs
      activeKey={values.tipo}
      onChange={(tipo) =>
        setValues({ ...values, tipo: tipo as TipoDePessoa, cpfCnpj: '', nomeUsuario: '', cpfUsuario: '' })
      }
      items={[
        { key: 'FISICA', label: 'Pessoa física' },
        { key: 'JURIDICA', label: 'Pessoa jurídica' },
      ]}
    />
  )
}

function DocumentoDoCaptador() {
  const { values, setFieldValue } = useFormikContext<ValoresDoCaptador>()
  const [buscando, setBuscando] = useState(false)
  const ehPessoaFisica = values.tipo === 'FISICA'

  async function preencherPeloCnpj(digitado: string) {
    if (ehPessoaFisica || digitado.replace(/\D/g, '').length !== 14) return

    setBuscando(true)
    const empresa = await buscarEmpresaPorCnpj(digitado)
    setBuscando(false)
    if (!empresa) return

    setFieldValue('nome', empresa.razaoSocial)
    setFieldValue('telefone', formatTelefone(empresa.telefone))
    setFieldValue('uf', empresa.uf)
  }

  return (
    <FormikInput
      name="cpfCnpj"
      label={ehPessoaFisica ? 'CPF' : 'CNPJ'}
      mask={ehPessoaFisica ? MASCARA_CPF : MASCARA_CNPJ}
      placeholder={ehPessoaFisica ? '000.000.000-00' : '00.000.000/0001-00'}
      onChangeValue={preencherPeloCnpj}
      suffix={
        <Spin
          indicator={<LoadingOutlined />}
          size="small"
          style={{ visibility: buscando ? 'visible' : 'hidden' }}
        />
      }
    />
  )
}

function NomeDoCaptador() {
  const { values } = useFormikContext<ValoresDoCaptador>()
  const rotulo = useRotuloDeFilho()

  if (values.tipo === 'JURIDICA') {
    return <FormikInput name="nome" label="Razão social" placeholder="Razão social da empresa" />
  }
  return <FormikInput name="nome" label="Nome completo" placeholder={`Nome do ${rotulo.singular}`} />
}

function SeletorDeUf() {
  const { values, setFieldValue } = useFormikContext<ValoresDoCaptador>()

  return (
    <Form.Item label="Estado (UF)">
      <Select
        allowClear
        showSearch
        value={values.uf || undefined}
        onChange={(uf) => setFieldValue('uf', uf ?? '')}
        placeholder="UF"
        options={BRAZILIAN_STATES.map((estado) => ({ label: estado, value: estado }))}
      />
    </Form.Item>
  )
}

function DadosDeAcesso() {
  const { values } = useFormikContext<ValoresDoCaptador>()

  return (
    <>
      <FormikInput name="email" label="E-mail de acesso" placeholder="email@empresa.com" />
      {values.tipo === 'JURIDICA' && (
        <>
          <FormikInput
            name="nomeUsuario"
            label="Nome do usuário"
            placeholder="Quem vai acessar o painel"
          />
          <FormikInput
            name="cpfUsuario"
            label="CPF do usuário"
            mask={MASCARA_CPF}
            placeholder="000.000.000-00"
          />
        </>
      )}
      <SenhaInicial />
    </>
  )
}

function SenhaInicial() {
  const { values } = useFormikContext<ValoresDoCaptador>()
  const senha = stripCpf(cpfDeAcesso(values)).slice(0, 6)
  if (senha.length < 6) return null

  return (
    <Text type="secondary" className="block">
      Entra com o CPF e a senha <strong>{senha}</strong> — os 6 primeiros dígitos.
    </Text>
  )
}
